import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOCAL_IDEAS_KEY = "voice_ideias_saved";

// Default categories (fallback)
export const DEFAULT_CATEGORIES = [
  { value: "Negócios", label: "Negócios" },
  { value: "Projetos", label: "Projetos" },
  { value: "Conteúdo", label: "Conteúdo" },
  { value: "Pessoal", label: "Pessoal" },
];

// For backwards compatibility
export const CATEGORIES = DEFAULT_CATEGORIES;

// Hook to fetch dynamic categories from API
export const useCategories = () => {
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  useEffect(() => {
    axios.get(`${API}/ideas/categories`)
      .then(res => {
        if (res.data.categories?.length > 0) {
          setCategories(res.data.categories);
        }
      })
      .catch(() => {});
  }, []);

  return categories;
};

// Helper to load ideas from localStorage
const loadLocalIdeas = () => {
  try {
    const saved = localStorage.getItem(LOCAL_IDEAS_KEY);
    if (saved) {
      const parsedIdeas = JSON.parse(saved);
      if (Array.isArray(parsedIdeas)) {
        return parsedIdeas;
      }
    }
  } catch (error) {
    console.error("Error loading local ideas:", error);
  }
  return [];
};

// Helper to save ideas to localStorage
const saveLocalIdeas = (ideas) => {
  try {
    localStorage.setItem(LOCAL_IDEAS_KEY, JSON.stringify(ideas));
  } catch (error) {
    console.error("Error saving local ideas:", error);
  }
};

export const useIdeas = () => {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load ideas based on auth status
  const loadIdeas = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (isAuthenticated) {
        // Load from API
        const response = await axios.get(`${API}/ideas`, {
          headers: getAuthHeaders(),
        });
        setIdeas(response.data);
      } else {
        // Load from localStorage
        setIdeas(loadLocalIdeas());
      }
    } catch (err) {
      console.error("Error loading ideas:", err);
      setError(err.response?.data?.detail || "Erro ao carregar ideias");
      // Fallback to local storage if API fails
      if (isAuthenticated) {
        setIdeas([]);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Load ideas on mount and when auth status changes
  useEffect(() => {
    loadIdeas();
  }, [loadIdeas]);

  // Create idea
  const createIdea = useCallback(async (title, content, category = "Pessoal") => {
    if (isAuthenticated) {
      // Save to API
      const response = await axios.post(
        `${API}/ideas`,
        { title, content, category },
        { headers: getAuthHeaders() }
      );
      setIdeas((prev) => [response.data, ...prev]);
      return response.data;
    } else {
      // Save to localStorage
      const newIdea = {
        id: Date.now().toString(),
        title,
        content,
        category,
        favorite: false,
        created_at: new Date().toISOString(),
        user_id: "local",
      };
      setIdeas((prev) => {
        const updated = [newIdea, ...prev];
        saveLocalIdeas(updated);
        return updated;
      });
      return newIdea;
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Update idea
  const updateIdea = useCallback(async (ideaId, updates) => {
    if (isAuthenticated) {
      // Update via API
      const response = await axios.put(
        `${API}/ideas/${ideaId}`,
        updates,
        { headers: getAuthHeaders() }
      );
      setIdeas((prev) =>
        prev.map((idea) => (idea.id === ideaId ? response.data : idea))
      );
      return response.data;
    } else {
      // Update in localStorage
      setIdeas((prev) => {
        const updated = prev.map((idea) =>
          idea.id === ideaId
            ? { ...idea, ...updates, updated_at: new Date().toISOString() }
            : idea
        );
        saveLocalIdeas(updated);
        return updated;
      });
      return ideas.find((i) => i.id === ideaId);
    }
  }, [isAuthenticated, getAuthHeaders, ideas]);

  // Delete idea
  const deleteIdea = useCallback(async (ideaId) => {
    if (isAuthenticated) {
      // Delete via API
      await axios.delete(`${API}/ideas/${ideaId}`, {
        headers: getAuthHeaders(),
      });
      setIdeas((prev) => prev.filter((idea) => idea.id !== ideaId));
    } else {
      // Delete from localStorage
      setIdeas((prev) => {
        const updated = prev.filter((idea) => idea.id !== ideaId);
        saveLocalIdeas(updated);
        return updated;
      });
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Toggle favorite
  const toggleFavorite = useCallback(async (ideaId) => {
    const idea = ideas.find((i) => i.id === ideaId);
    if (!idea) return;
    const newFavorite = !idea.favorite;

    if (isAuthenticated) {
      await axios.put(
        `${API}/ideas/${ideaId}`,
        { favorite: newFavorite },
        { headers: getAuthHeaders() }
      );
      setIdeas((prev) =>
        prev.map((i) => (i.id === ideaId ? { ...i, favorite: newFavorite } : i))
      );
    } else {
      setIdeas((prev) => {
        const updated = prev.map((i) =>
          i.id === ideaId ? { ...i, favorite: newFavorite } : i
        );
        saveLocalIdeas(updated);
        return updated;
      });
    }
  }, [isAuthenticated, getAuthHeaders, ideas]);

  // Filter ideas by category
  const filterByCategory = useCallback((category) => {
    if (!category) return ideas;
    return ideas.filter((idea) => idea.category === category);
  }, [ideas]);

  return {
    ideas,
    loading,
    error,
    createIdea,
    updateIdea,
    deleteIdea,
    toggleFavorite,
    loadIdeas,
    filterByCategory,
    isLocal: !isAuthenticated,
  };
};

export default useIdeas;
